import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { v4 as uuidv4 } from "uuid";
import { db, Timestamp } from "api/firebase";
import {
  BackgroundTaskStatus,
  IBackgroundTask,
} from "ts/api/background-pubsub-task";
import backoff from "backoff";
import { registerStore } from "store/utils/reset-store.utils";

export type WrappedTask = Array<
  Record<string, any> & { commonOperationId: string; operationId: string }
>;

export type StartWatchOption = {
  errorText: string;
  successText: string;
  inProcessText: string;
  showId: string;
  taskId?: string;
  standId?: string;
};

export type InitBackgroundTaskArg = {
  tasks: any[];
  options: StartWatchOption;
};

export type ListItem = {
  status: BackgroundTaskStatus;
  commonOperationId: string;
} & StartWatchOption;

type State = {
  tasks: Map<string, Map<string, BackgroundTaskStatus>>;
  listItems: Array<ListItem>;
};

type Action = {
  initBackgroundTask: (arg: InitBackgroundTaskArg) => Promise<WrappedTask>;

  autoClearNotification: (commonOperationId: string) => void;
  addItemToNotification: (
    commonOperationId: string,
    status: BackgroundTaskStatus
  ) => void;

  startWatch: (commonOperationId: string, params: StartWatchOption) => void;

  clear: () => void;
};

export const useBackgroundTask = create(
  devtools<State & Action>((set, get) => ({
    tasks: new Map(),
    listItems: [],

    clear: (): void => {
      set({
        tasks: new Map(),
        listItems: [],
      });
    },

    autoClearNotification: (commonOperationId: string) => {
      setTimeout(() => {
        set({
          listItems: get().listItems.filter((data) => {
            return data.commonOperationId !== commonOperationId;
          }),
        });
      }, 5_000);
    },

    addItemToNotification: (
      commonOperationId: string,
      newStatus: BackgroundTaskStatus
    ) => {
      set({
        listItems: get().listItems.map((data) => {
          let { status } = data;

          if (data.commonOperationId === commonOperationId) {
            status = newStatus;
          }

          return {
            ...data,
            status,
          };
        }),
      });

      get().autoClearNotification(commonOperationId);
    },

    startWatch: (
      commonOperationId: string,
      options: StartWatchOption
    ): void => {
      const fibonacciBackoff = backoff.fibonacci({
        initialDelay: 500,
        maxDelay: 60_000 * 5,
      });

      fibonacciBackoff.on("fail", () => {
        get().addItemToNotification(
          commonOperationId,
          BackgroundTaskStatus.ERROR
        );
      });

      fibonacciBackoff.on("ready", async () => {
        const childTasks:
          | Map<string, BackgroundTaskStatus>
          | undefined = get().tasks.get(commonOperationId);

        if (!childTasks) {
          return;
        }

        const childTasksArray = Array.from(childTasks.entries());

        for (const childTasks of childTasksArray) {
          const [operationId, status] = childTasks;

          const taskRef = db
            .collection("backgroundTasks")
            .doc(commonOperationId)
            .collection("operations")
            .doc(operationId);

          // eslint-disable-next-line no-await-in-loop
          const taskSnapshot = await taskRef.get();

          const taskData = taskSnapshot.data();

          if (taskSnapshot.exists && taskData) {
            get()
              .tasks.get(taskData.commonOperationId)
              ?.set(taskData.operationId, taskData.status);
          }
        }

        const isAllTaskIsCompleted = Array.from(childTasks.entries()).every(
          ([, status]) => status === BackgroundTaskStatus.COMPLETED
        );

        if (isAllTaskIsCompleted) {
          get().addItemToNotification(
            commonOperationId,
            BackgroundTaskStatus.COMPLETED
          );

          return;
        }

        const isSomeError = Array.from(childTasks.entries()).some(
          ([, status]) => status === BackgroundTaskStatus.ERROR
        );

        if (isSomeError) {
          get().addItemToNotification(
            commonOperationId,
            BackgroundTaskStatus.ERROR
          );
          return;
        }

        fibonacciBackoff.backoff();
      });

      fibonacciBackoff.backoff();
    },

    initBackgroundTask: async ({
      tasks,
      options,
    }: InitBackgroundTaskArg): Promise<
      Array<
        Record<string, any> & { commonOperationId: string; operationId: string }
      >
    > => {
      const commonOperationId = uuidv4();

      if (tasks.length === 0) {
        set({
          listItems: [
            ...get().listItems,
            {
              status: BackgroundTaskStatus.COMPLETED,
              commonOperationId,
              ...options,
            },
          ],
        });

        get().autoClearNotification(commonOperationId);

        return [];
      }

      const newTasks: Array<
        Record<string, any> & { commonOperationId: string; operationId: string }
      > = tasks.map((data) => {
        return {
          ...data,
          commonOperationId,
          operationId: uuidv4(),
        };
      });

      const batch = db.batch();

      for (const newTask of newTasks) {
        batch.set(db.collection("backgroundTasks").doc(commonOperationId), {
          ...options,
          timestamp: Timestamp.now(),
        });

        const taskRef = db
          .collection("backgroundTasks")
          .doc(commonOperationId)
          .collection("operations")
          .doc(newTask.operationId);

        const operation: IBackgroundTask = {
          status: BackgroundTaskStatus.CREATED,
          message: null,
          stack: null,
          commonOperationId,
          operationId: newTask.operationId,
          timestamp: Timestamp.now(),
        };

        batch.set(taskRef, operation);

        if (!get().tasks.has(commonOperationId)) {
          const task: Map<string, BackgroundTaskStatus> = new Map();

          task.set(newTask.operationId, BackgroundTaskStatus.CREATED);

          get().tasks.set(commonOperationId, task);
        } else {
          get()
            .tasks.get(commonOperationId)
            ?.set(newTask.operationId, BackgroundTaskStatus.CREATED);
        }
      }

      await batch.commit();

      get().startWatch(commonOperationId, options);

      set({
        listItems: [
          ...get().listItems,
          {
            status: BackgroundTaskStatus.CREATED,
            commonOperationId,
            ...options,
          },
        ],
      });

      return newTasks;
    },
  }))
);

registerStore(useBackgroundTask);
